import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { MeetingRecordingInterface } from '@/components/meetings/MeetingRecordingInterface';

interface ConductMeetingPhase2Props {
  taskId: string;
  unaId: string;
  task: any;
  phaseData: any;
  onPhaseComplete?: () => void;
}

/**
 * Phase 2: Conduct & Record Meeting
 * Wrapper for MeetingRecordingInterface - live recording of attendees, agenda, motions, action items
 */
export function ConductMeetingPhase2({ taskId, unaId, task, phaseData, onPhaseComplete }: ConductMeetingPhase2Props) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [meetingMinutesId, setMeetingMinutesId] = useState<string | null>(null);

  const workflowData = task?.workflow_data || {};

  const normalizeMeetingTime = (timeValue: unknown): string => {
    if (typeof timeValue !== 'string' || !timeValue.trim()) return '09:00:00';

    const time = timeValue.trim();
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(time) && !/am|pm/i.test(time)) {
      return time.split(':').length === 2 ? `${time}:00` : time;
    }

    const match = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return '09:00:00';

    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3].toUpperCase();

    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;

    return `${hours.toString().padStart(2, '0')}:${minutes}:00`;
  };

  useEffect(() => {
    loadOrCreateMeetingRecord();
  }, [taskId, unaId]);

  async function loadOrCreateMeetingRecord() {
    try {
      setLoading(true);
      
      // First check if meeting_minutes record already exists for this task
      const { data: existingMinutes, error: fetchError } = await supabase
        .from('meeting_minutes')
        .select('id')
        .eq('task_id', taskId)
        .maybeSingle();

      if (fetchError) {
        console.error('Error fetching meeting minutes:', fetchError);
      }

      if (existingMinutes) {
        console.log('✅ Found existing meeting_minutes:', existingMinutes.id);
        setMeetingMinutesId(existingMinutes.id);
        setLoading(false);
        return;
      }

      // Create new meeting_minutes record if none exists
      console.log('Creating new meeting_minutes record for task:', taskId);
      
      // Extract meeting data from workflow_data
      const meetingType = workflowData.meeting_type || 'Board Meeting';
      const meetingDate = workflowData.meeting_date || new Date().toISOString().split('T')[0];
      const meetingLocation = workflowData.meeting_location || '';
      const meetingPurpose = workflowData.meeting_purpose || '';
      const meetingTime = normalizeMeetingTime(workflowData.meeting_time || workflowData.meeting_start_time);
      
      // Build expected attendees from required_attendees and optional_attendees
      const requiredAttendees = workflowData.required_attendees || [];
      const optionalAttendees = workflowData.optional_attendees || [];
      const expectedAttendeeIds = [
        ...requiredAttendees.map((a: any) => a.id || a),
        ...optionalAttendees.map((a: any) => a.id || a)
      ].filter(Boolean);
      
      // Handle external guests
      const externalGuests = workflowData.external_guests || [];
      const specialGuests = externalGuests.map((g: any) => ({
        name: g.name || g.guest_name || 'Guest',
        affiliation: g.affiliation || g.guest_organization || '',
        type: g.type || 'external_guest'
      }));

      // Get meeting conductor and recording officer
      const meetingConductorId = workflowData.meeting_conductor || null;
      const recordingOfficerId = workflowData.recording_officer || null;

      const { data: newMinutes, error: createError } = await supabase
        .from('meeting_minutes')
        .insert({
          una_id: unaId,
          task_id: taskId,
          meeting_type: meetingType,
          meeting_date: meetingDate,
          meeting_location: meetingLocation,
          meeting_purpose: meetingPurpose,
          meeting_start_time: meetingTime,
          expected_attendees: expectedAttendeeIds,
          special_guests: specialGuests,
          presiding_officer_id: meetingConductorId,
          recording_secretary_id: recordingOfficerId,
          status: 'recording',
          quorum_required: workflowData.quorum_required || false,
          quorum_threshold: workflowData.quorum_threshold || null,
          has_transcript: workflowData.enable_transcription || false,
        })
        .select('id')
        .single();

      if (createError) {
        console.error('Error creating meeting minutes:', createError);
        toast({
          title: 'Error',
          description: 'Could not create meeting record. Please try again.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      console.log('✅ Created new meeting_minutes:', newMinutes.id);
      setMeetingMinutesId(newMinutes.id);

      await supabase
        .from('task_workflows')
        .update({ meeting_minutes_id: newMinutes.id })
        .eq('task_id', taskId);
    } catch (error) {
      console.error('Error in loadOrCreateMeetingRecord:', error);
      toast({
        title: 'Error',
        description: 'Failed to load meeting data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-2">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="text-sm text-muted-foreground">Loading meeting recording interface...</p>
      </div>
    );
  }

  if (!meetingMinutesId) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">
            Unable to load meeting record. Please refresh and try again.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Phase Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CardTitle>Phase 2: Conduct & Record Meeting</CardTitle>
                <Badge>Current Phase</Badge>
              </div>
              <p className="text-muted-foreground">
                Use the recording interface to capture attendance, discussions, motions, and action items during the meeting
              </p>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Meeting Recording Interface */}
      <MeetingRecordingInterface
        meetingId={meetingMinutesId}
        unaId={unaId}
        taskId={taskId}
        initialData={workflowData}
        onPhaseComplete={onPhaseComplete}
        onSave={(data) => {
          console.log('Meeting data saved:', data);
        }}
      />
    </div>
  );
}
